import {
  ApplePay,
  PayPal,
  PaymentData,
  PaymentCompletedData,
  UIElement,
  ApplePayConfiguration,
  AdyenCheckoutError,
  GooglePay,
  GooglePayConfiguration,
  Klarna,
  KlarnaConfiguration,
  AdditionalDetailsActions,
  PaymentResponseData,
  CheckoutAdvancedFlowResponse,
  PayPalConfiguration,
  UIElementProps,
  BeforeSubmitActions,
  SubmitData,
  SubmitActions
} from "@adyen/adyen-web"
import { AdditionalData3DSecure } from "@adyen/api-library/lib/src/typings/payment/additionalData3DSecure"
import { MutableRefObject } from "react"
import { useTranslation } from "react-i18next"
import { useCheckoutState } from "../checkout/CheckoutState"
import { AdyenPaymentMethodType } from "../checkout/payment/validation/AdyenPaymentValidation"
import { success } from "../checkout/validation/FormValidator"
import { ValidationResult, error } from "../checkout/validation/ValidationError"
import { LinkProps } from "../components/Link"
import { useProcessingLink } from "../components/Links"
import { ErrorType } from "../error/ErrorType"
import { useRouter } from "../routing/useRouter"
import logger from "../utils/logger"

import { useAdyenCheckout, useAdyenBrowserClient } from "./AdyenCheckoutHooks"
import {
  useAdyenPaymentData,
  useSelectedAdyenPaymentType,
  useAdyenPayment,
  usePlannedAdyenAmount
} from "./AdyenState"

interface AdyenPaymentStrategy<T> {
  createCheckout: (options?: T) => void
}

type PayFn = (paymentData: PaymentData) => Promise<CheckoutAdvancedFlowResponse>

interface PaymentContext {
  payFn: PayFn
  onPaymentSuccessful: () => void
}

type PromiseResolve = typeof Promise.resolve

type PromiseReject = typeof Promise.reject

export const useSchemePaymentStrategy: (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => { pay: () => ValidationResult } = (paymentContainer, formValidation) => {
  const { t } = useTranslation("checkout")
  const adyenPaymentData = useAdyenPaymentData()
  const { createDynamicCheckout } = useAdyenCheckout()
  const { payFn, onPaymentSuccessful } = useAdyenPaymentContext()
  const adyenBrowserClient = useAdyenBrowserClient()

  return {
    pay: async () => {
      logger.info("Paying with credit card")
      if (!adyenPaymentData) {
        logger.warn(`Missing Adyen Payment Data ${adyenPaymentData}`)
        return error(ErrorType.PaymentError, {
          title: t("paymentFailedTitle"),
          message: t("paymentFailedMessage")
        })
      }
      return formValidation()
        .then(() => payFn(adyenPaymentData))
        .then(response => {
          if (response.resultCode === "Authorised") {
            onPaymentSuccessful()
          } else if (response.action && paymentContainer.current) {
            createDynamicCheckout(
              paymentContainer,
              it =>
                Promise.resolve(
                  it.createFromAction(response.action!, {
                    challengeWindowSize: "05"
                  })
                ),
              {
                onPaymentCompleted(
                  data: PaymentCompletedData,
                  element?: UIElement
                ) {
                  console.info("Payment completed: scheme")
                  onPaymentSuccessful()
                },
                onAdditionalDetails: (
                  state: {
                    data: {
                      details: AdditionalData3DSecure
                    }
                  },
                  element: UIElement,
                  actions: AdditionalDetailsActions
                ): void => {
                  adyenBrowserClient
                    .details(state.data?.details, response?.action?.paymentData)
                    .then(it => actions.resolve(it))
                    .catch(err => logger.warn(err))
                }
              }
            )
          }
        })
        .then(success)
    }
  }
}

type VoidFn = () => void

const usePaypalPaymentStrategy: (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => AdyenPaymentStrategy<PayPalConfiguration> = (
  paymentContainer,
  formValidation
) => {
  const { t } = useTranslation("checkout")

  const { createDynamicCheckout, onPaymentError } = useAdyenCheckout()

  const { payFn, onPaymentSuccessful } = useAdyenPaymentContext()

  const paypalErrorMessage = {
    title: t("paypalErrorTitle"),
    message: t("paypalErrorMessage")
  }

  return {
    createCheckout: () =>
      createDynamicCheckout(
        paymentContainer,
        core => {
          const options: PayPalConfiguration = {
            style: {
              // Optional configuration for PayPal payment buttons.
              // layout: "vertical",
              // color: "blue"
            },
            cspNonce: "MY_CSP_NONCE",
            onInit: (
              data,
              actions: { enable: VoidFn; disable: VoidFn } = {
                enable: () => logger.warn("Enabling paypal button"),
                disable: () => logger.warn("Disabling paypal button")
              }
            ) => {
              actions.disable()
              actions.enable()
            },
            onClick: async () => {
              formValidation().catch(e => {
                logger.warn("Rejecting Paypal payment", e)
                onPaymentError(e)
              })
            },
            onSubmit: async (
              state: SubmitData,
              component: UIElement,
              actions: SubmitActions
            ) => {
              logger.info("Setting paypal state", { state, component })
              const payload = {
                paymentMethod: {
                  type: "paypal",
                  subtype: "sdk"
                },
                clientStateDataIndicator: true
              }

              return payFn({
                ...(state.data || {}),
                ...payload
              })
                .then((response): ValidationResult => {
                  if (
                    response.resultCode !== "Authorised" &&
                    response.resultCode !== "Pending"
                  ) {
                    actions.reject()
                    return error(ErrorType.PaymentError, paypalErrorMessage)
                  }
                  if (response.action) {
                    component.handleAction(response.action)
                  }
                  //
                  actions.resolve(response)
                  return success()
                })
                .catch(e => {
                  logger.warn("PayPal payment failed")
                  logger.warn(e)
                  actions.reject(e)
                  return error(ErrorType.PaymentError, paypalErrorMessage)
                })
            },
            onPaymentCompleted: (result: unknown, component: UIElement) => {
              logger.info("Payment completed: ", result)
              onPaymentSuccessful()
            },
            blockPayPalCreditButton: true,
            blockPayPalPayLaterButton: true
          }
          return Promise.resolve(new PayPal(core, options))
        },
        {}
      )
  }
}

export const useApplePayPaymentStrategy: (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => AdyenPaymentStrategy<ApplePayConfiguration> = (
  paymentContainer,
  formValidation
) => {
  const { createDynamicCheckout } = useAdyenCheckout()
  const { onPaymentSuccessful } = useAdyenPaymentContext()
  const amount = usePlannedAdyenAmount()

  return {
    createCheckout: (extras?: ApplePayConfiguration) => {
      createDynamicCheckout(
        paymentContainer,
        core => {
          const options: ApplePayConfiguration = {
            onClick: (resolve: PromiseResolve, reject: PromiseReject) => {
              return formValidation()
                .then(resolve)
                .catch(e => {
                  reject()
                  if (core.options.onError) {
                    core.options.onError({
                      cause: e
                    } as AdyenCheckoutError)
                  }
                })
            },
            onPaymentCompleted: result => {
              onPaymentSuccessful()
            },
            amount,
            ...extras
          }
          const element = new ApplePay(core, options)
          return element.isAvailable().then(() => element)
        },
        {}
      )
    }
  }
}

const useGooglePayPaymentStrategy: (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => AdyenPaymentStrategy<UIElementProps> = (
  paymentContainer,
  formValidation
) => {
  const checkoutState = useCheckoutState()
  const { createDynamicCheckout } = useAdyenCheckout()
  const { onPaymentSuccessful } = useAdyenPaymentContext()
  const amount = usePlannedAdyenAmount()

  return {
    createCheckout: () =>
      createDynamicCheckout(
        paymentContainer,
        core => {
          const options: GooglePayConfiguration = {
            onClick: (resolve: PromiseResolve, reject: PromiseReject) => {
              return formValidation().then(resolve).catch(reject)
            },
            onChange: () => {
              checkoutState.hideOverview()
            },
            onPaymentCompleted: (result, element) => {
              onPaymentSuccessful()
            },
            amount
          }
          const element = new GooglePay(core, options)
          return element.isAvailable().then(() => element)
        },
        {}
      )
  }
}

const useKlarnaPaymentStrategy: (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => AdyenPaymentStrategy<UIElementProps> = (
  paymentContainer,
  formValidation
) => {
  const { createDynamicCheckout, onPaymentError } = useAdyenCheckout()
  const { onPaymentSuccessful } = useAdyenPaymentContext()
  const adyenPaymentType = useSelectedAdyenPaymentType()

  return {
    createCheckout: () =>
      createDynamicCheckout(
        paymentContainer,
        core => {
          const options: KlarnaConfiguration = {
            useKlarnaWidget: true,
            type: adyenPaymentType,
            beforeSubmit: async (
              state: PaymentData,
              component: UIElement,
              actions: BeforeSubmitActions
            ) => {
              formValidation()
                .then(() => {
                  logger.info("Form validation successful, resolving: ", state)
                  actions.resolve(state)
                })
                .catch(e => {
                  logger.warn("Rejecting Klarna payment")
                  actions.reject()
                  onPaymentError(e)
                })
            },
            onComplete: () => {
              logger.info("Klarna onComplete")
            },
            onPaymentCompleted: (data: PaymentCompletedData, element) => {
              onPaymentSuccessful()
            }
          }
          return Promise.resolve(new Klarna(core, options))
        },
        {}
      )
  }
}

const useAdyenPaymentContext = (): PaymentContext => {
  const adyenBrowserClient = useAdyenBrowserClient()
  const adyenPayment = useAdyenPayment() || { id: "", session: "" }

  const processingLink = useProcessingLink()
  const router = useRouter()

  return {
    payFn: (paymentData: PaymentData): Promise<PaymentResponseData> =>
      adyenBrowserClient.pay(adyenPayment?.id, {
        returnUrl: processingLink.href,
        ...paymentData
      }),
    onPaymentSuccessful: () => {
      router.push(processingLink.href, processingLink.as)
    }
  }
}

export const useAdyenValidation = (
  paymentContainer: MutableRefObject<HTMLDivElement | null>,
  formValidation: () => Promise<LinkProps | undefined>
) => {
  const adyenPaymentType = useSelectedAdyenPaymentType()

  const paypal = usePaypalPaymentStrategy(paymentContainer, formValidation)
  const applepay = useApplePayPaymentStrategy(paymentContainer, formValidation)
  const googlepay = useGooglePayPaymentStrategy(
    paymentContainer,
    formValidation
  )
  const klarna = useKlarnaPaymentStrategy(paymentContainer, formValidation)

  const adyenPaymentStrategy: Record<
    Exclude<AdyenPaymentMethodType, "scheme">,
    AdyenPaymentStrategy<UIElementProps>
  > = {
    paypal,
    applepay,
    googlepay,
    klarna,
    klarna_account: klarna
  }

  return {
    createCheckout: adyenPaymentStrategy[adyenPaymentType || ""]?.createCheckout
  }
}
